import React, { useCallback, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  useVideoMetadata,
  useVideoPlayback,
} from '../context/VideoEditorContext';
import { useContainerMeasurement } from '../hooks/useContainerMeasurement';
import { useThumbnailExtractor } from '../hooks/useThumbnailExtractor';
import { useVideoThumbnails } from '../hooks/useVideoThumbnails';
import { FILMSTRIP_STRIP_HEIGHT } from '../utils/constants';
import { timeToAbsolutePixel } from '../utils/selectionCalculations';
import { VideoSelection } from '../utils/videoUtils';
import SelectionIndicator from './SelectionIndicator';
import Thumbnail from './Thumbnail';

interface FilmStripSliderProps {
  selection: VideoSelection;
  onSelectionChange: (selection: VideoSelection) => void;
  className?: string;
  onSeek?: (time: number) => void;
}

export default function FilmStripSlider({
  selection,
  onSelectionChange,
  className = '',
  onSeek,
}: FilmStripSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { containerWidth } = useContainerMeasurement(containerRef);

  const { videoSrc, videoMetadata } = useVideoMetadata();
  const { isPlaying, handlePlayPause } = useVideoPlayback();

  const src = videoSrc ?? '';

  const { thumbnails, isReady } = useVideoThumbnails({
    videoMetadata,
    stripHeight: FILMSTRIP_STRIP_HEIGHT,
    containerWidth,
    thumbnailsPerSecond: 0.5,
  });

  const { frameUrls, hasError: extractionFailed } = useThumbnailExtractor({
    videoSrc: src || null,
    thumbnails,
  });

  const handleSelectionMove = useCallback(
    (startTime: number) => {
      const newEndTime = startTime + selection.duration;
      onSelectionChange({
        startTime,
        endTime: newEndTime,
        duration: selection.duration,
      });
    },
    [selection.duration, onSelectionChange]
  );

  const actualThumbnailHeight =
    thumbnails.length > 0 ? thumbnails[0].height : FILMSTRIP_STRIP_HEIGHT;

  const isLoaded = videoMetadata && isReady;

  const selectionStartPx = isLoaded
    ? Math.max(
        0,
        timeToAbsolutePixel(
          selection.startTime,
          videoMetadata.duration,
          containerWidth
        )
      )
    : 0;

  const selectionEndPx = isLoaded
    ? Math.min(
        timeToAbsolutePixel(
          selection.endTime,
          videoMetadata.duration,
          containerWidth
        ),
        containerWidth
      )
    : 0;

  return (
    <div
      ref={containerRef}
      className={twMerge('relative w-full mb-5', className)}
    >
      {!isLoaded ? (
        <div
          className="bg-gray-800 rounded-lg flex items-center justify-center w-full"
          style={{ height: FILMSTRIP_STRIP_HEIGHT }}
        >
          <div className="text-white text-sm">
            {!videoMetadata
              ? 'Loading video metadata...'
              : 'Loading thumbnails...'}
          </div>
        </div>
      ) : (
        <div
          className="relative w-full overflow-visible"
          style={{
            height: actualThumbnailHeight,
          }}
        >
          <div
            data-testid="filmstrip-scroll-container"
            className="overflow-hidden h-full"
          >
            <div
              className="flex select-none relative"
              style={{
                width: containerWidth,
              }}
            >
              {thumbnails.map((thumbnail, index) => (
                <div
                  key={`${src}-${thumbnail.timeInSeconds}-${index}`}
                  className={`flex-shrink-0 ${
                    index === 0 ? 'overflow-hidden' : ''
                  } ${
                    index === thumbnails.length - 1 ? 'overflow-hidden' : ''
                  }`}
                  style={{
                    width: thumbnail.width,
                  }}
                >
                  <Thumbnail
                    thumbnail={thumbnail}
                    imageSrc={!extractionFailed ? frameUrls[index] : undefined}
                    videoSrc={extractionFailed ? src : undefined}
                  />
                </div>
              ))}

              {/* Unselected left overlay */}
              <div
                className="absolute top-0 left-0 bg-black bg-opacity-30 pointer-events-none"
                style={{
                  width: selectionStartPx,
                  height: actualThumbnailHeight,
                }}
              />

              {/* Unselected right overlay */}
              <div
                className="absolute top-0 bg-black bg-opacity-30 pointer-events-none"
                style={{
                  left: selectionEndPx,
                  width: Math.max(0, containerWidth - selectionEndPx),
                  height: actualThumbnailHeight,
                }}
              />
            </div>

            <SelectionIndicator
              selection={selection}
              videoDuration={videoMetadata.duration}
              stripWidth={containerWidth}
              stripHeight={actualThumbnailHeight}
              onSelectionMove={handleSelectionMove}
              onSelectionChange={onSelectionChange}
              onSeek={onSeek}
              onPlayPause={handlePlayPause}
              isPlaying={isPlaying}
            />
          </div>
        </div>
      )}
    </div>
  );
}
